/** @jsxImportSource @emotion/react */
import { useMemo, useCallback } from 'react';
import { Dropdown, Button, message, Avatar } from 'antd';
import { CaretDownOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { importAllImages } from '@/shared/helpers/images-importer';
import { UserOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';

import GlobalNotification from '../GlobalNotification';
import style from './style';

// images providers
const shareImageContexts = (require as any).context(
    '@shared/imgs/',
    false,
    /\.(png|jpe?g|svg|webp)$/
);
const imageContexts = (require as any).context('./imgs/', false, /\.(png|jpe?g|svg|webp)$/);
const shareImages = importAllImages(shareImageContexts);
const images = importAllImages(imageContexts);

const OUTSIDE_HOST = process.env.REACT_APP_VUE_CLIENT_URL;
const BLOG_URL = 'https://propertigrab.wordpress.com';

const generatePictureElem = (assetName: string) => {
    return (
        <picture css={style.navPicIcon}>
            <source srcSet={images[`${assetName}.webp`]} type="image/webp" />
            <img src={images[`${assetName}.png`]} alt="" />
        </picture>
    );
};

const WalletIcon = () => {
    return (
        <picture css={style.navPicIcon}>
            <svg
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ display: 'block' }}
            >
                <rect x="2.5" y="5.5" width="12" height="9" rx="1.5" stroke="#595959" strokeWidth="1.5" fill="none" />
                <path d="M2.5 9H14.5" stroke="#595959" strokeWidth="1.5" strokeLinecap="round" />
                <circle cx="11" cy="11.5" r="2" stroke="#595959" strokeWidth="1.2" fill="none" />
                <path d="M11 10.3V12.7" stroke="#595959" strokeWidth="1.2" strokeLinecap="round" />
                <path d="M9.8 11.5H12.2" stroke="#595959" strokeWidth="1.2" strokeLinecap="round" />
            </svg>
        </picture>
    );
};

type NavbarViewPropsType = {
    currentLang: string | null;
    isAlreadyLogin: boolean;
    onLanguageSubMenuClicked: () => void;
    onLogout: () => void;
    onShowSugesstionModal: () => void;
};

const NavbarView = (props: NavbarViewPropsType) => {
    const [t] = useTranslation('navbar');

    const handleSuggession = useCallback(() => {
        if (props.isAlreadyLogin) {
            window.location.href = `${OUTSIDE_HOST}?sugession=yes`;
        } else {
            message.info(t('advicement_login_first'));
        }
    }, [props.isAlreadyLogin, t]);

    const searchNavigationItems: MenuProps['items'] = useMemo(
        () => [
            {
                key: 'search-buy',
                icon: generatePictureElem('ico-beli'),
                label: (
                    <a href={`${OUTSIDE_HOST}/property-list?usage=sell&type=propertyAdvertisement`}>
                        {t('search.submenu.buy')}
                    </a>
                ),
            },
            {
                key: 'search-rent',
                icon: generatePictureElem('ico-sewa'),
                label: (
                    <a href={`${OUTSIDE_HOST}/property-list?usage=rent&type=propertyAdvertisement`}>
                        {t('search.submenu.rent')}
                    </a>
                ),
            },
            {
                key: 'search-new-property',
                icon: generatePictureElem('ico-propertibaru'),
                label: (
                    <a
                        href={`${OUTSIDE_HOST}/property-list?usage=sell,rent&type=projectAdvertisement`}
                    >
                        {t('search.submenu.new_property')}
                    </a>
                ),
            },
            {
                key: 'search-property-agent',
                icon: generatePictureElem('ico-agenproperti'),
                label: (
                    <a href={`${OUTSIDE_HOST}/agent-list?usage=self&type=agentAdvertisement`}>
                        {t('search.submenu.property_agent')}
                    </a>
                ),
            },
            { type: 'divider' },
            {
                key: 'search-all',
                icon: generatePictureElem('ico-semua'),
                label: (
                    <a
                        href={`${OUTSIDE_HOST}/property-list?usage=sell,rent&type=propertyAdvertisement,projectAdvertisement`}
                    >
                        {t('search.submenu.find_all')}
                    </a>
                ),
            },
        ],
        [t]
    );

    const otherNavigationItems: MenuProps['items'] = useMemo(
        () => {
            const items: MenuProps['items'] = [
                {
                    key: 'other-forum',
                    icon: generatePictureElem('ico-forum'),
                    label: <a href={`${OUTSIDE_HOST}/forum`}>{t('other.submenu.forum')}</a>,
                },
                {
                    key: 'other-blog',
                    icon: generatePictureElem('ico-blog'),
                    label: (
                        <a href={BLOG_URL} target="_blank" rel="noreferrer">
                            {t('other.submenu.blog')}
                        </a>
                    ),
                },
                {
                    key: 'other-plan-and-pricing',
                    icon: generatePictureElem('ico-tentangkami'),
                    label: (
                        <a href={`${OUTSIDE_HOST}/plan-and-pricing`}>
                            {t('other.submenu.plan_and_pricing')}
                        </a>
                    ),
                },
            ];

            /* Only show Top Up Wallet menu if user is logged in */
            if (props.isAlreadyLogin) {
                items.push({
                    key: 'other-top-up-wallet',
                    icon: <WalletIcon />,
                    label: (
                        <a href={`${OUTSIDE_HOST}/member-area/my-wallet?ref=top-nav`}>
                            {t('other.submenu.top_up_wallet')}
                        </a>
                    ),
                });
            }

            items.push(
                {
                    key: 'member-suggestion',
                    icon: generatePictureElem('suggestion'),
                    label: (
                        <span onClick={handleSuggession} style={{ cursor: 'pointer' }}>
                            {t('member.submenu.suggestion')}
                        </span>
                    ),
                },
                {
                    key: 'other-language',
                    onClick: props.onLanguageSubMenuClicked,
                    label: (
                        <div css={style.languageToggle}>
                            <Avatar shape="square" size="small" css={style.languageAvatar}>
                                {props.currentLang}
                            </Avatar>
                            <span css={style.languageText}>
                                {props.currentLang === 'id' ? 'Change Language' : 'Ganti Bahasa'}
                            </span>
                        </div>
                    ),
                }
            );

            return items;
        },
        [t, props.currentLang, props.onLanguageSubMenuClicked, props.isAlreadyLogin, handleSuggession]
    );

    const comproNavigationItems: MenuProps['items'] = useMemo(
        () => [
            {
                key: 'compro-about-us',
                icon: generatePictureElem('ico-tentangkami'),
                label: <a href={`${OUTSIDE_HOST}/about-us`}>{t('compro.submenu.about_us')}</a>,
            },
            {
                key: 'compro-hiw',
                icon: generatePictureElem('ico-carakerja'),
                label: <a href={`${OUTSIDE_HOST}/how-it-work`}>{t('compro.submenu.hiw')}</a>,
            },
            {
                key: 'compro-faq',
                icon: generatePictureElem('ico-faq'),
                label: <a href={`${OUTSIDE_HOST}/faq`}>{t('compro.submenu.faq')}</a>,
            },
        ],
        [t]
    );

    const investmentNavigationItems: MenuProps['items'] = useMemo(
        () => [
            {
                key: 'investment-crowdfund',
                icon: generatePictureElem('ico-properti'),
                label: (
                    <a
                        href={`${OUTSIDE_HOST}/crowdfund-list?usage=crowdfund&type=propertyAdvertisement,projectAdvertisement&quickSearchCrowdfund=`}
                    >
                        {t('investment.submenu.crowdfund')}
                    </a>
                ),
            },
            {
                key: 'investment-marketplace',
                icon: generatePictureElem('ico-properti'),
                label: (
                    <a
                        href={`${OUTSIDE_HOST}/marketplace-list?usage=crowdfund&fundingStatus=marketplace&type=propertyAdvertisement,projectAdvertisement&quickSearchMarketplace=`}
                    >
                        {t('investment.submenu.marketplace')}
                    </a>
                ),
            },
        ],
        [t]
    );

    const advertisementNavigationItems: MenuProps['items'] = useMemo(
        () => [
            {
                key: 'solutions-sell',
                icon: generatePictureElem('ico-ads-sell'),
                label: (
                    <a href={`${OUTSIDE_HOST}/create-property-solution/sell`}>
                        {t('solution.submenu.sell')}
                    </a>
                ),
            },
            {
                key: 'solutions-rent',
                icon: generatePictureElem('ico-ads-rent'),
                label: (
                    <a href={`${OUTSIDE_HOST}/create-property-solution/rent`}>
                        {t('solution.submenu.rent')}
                    </a>
                ),
            },
            {
                key: 'solutions-new-property',
                icon: generatePictureElem('ico-ads-new'),
                label: (
                    <a href={`${OUTSIDE_HOST}/create-project-solution`}>
                        {t('solution.submenu.new_property')}
                    </a>
                ),
            },
            {
                key: 'solutions-professional',
                icon: generatePictureElem('ico-ads-pro'),
                label: (
                    <Link to="/p/area/business-detail">{t('solution.submenu.professional')}</Link>
                ),
            },
        ],
        [t]
    );

    const membershipAreaNavigationItems: MenuProps['items'] = useMemo(
        () => [
            {
                key: 'member-member-area',
                icon: generatePictureElem('memberarea'),
                label: (
                    <a href={`${OUTSIDE_HOST}/member-area`}>{t('member.submenu.member_area')}</a>
                ),
            },
            {
                key: 'member-professional-area',
                icon: generatePictureElem('ico-blog'),
                label: <Link to="/p/area">{t('member.submenu.professional_area')}</Link>,
            },
            {
                key: 'member-logout',
                icon: generatePictureElem('logout'),
                label: <span>{t('member.submenu.logout')}</span>,
                onClick: props.onLogout,
            },
        ],
        [t, props.onLogout]
    );

    const renderLoginButton = () => {
        if (props.isAlreadyLogin) {
            return null;
        } else {
            return (
                <div className="dropdown">
                    <a href={`${OUTSIDE_HOST}/login`}>
                        <Button type="primary" css={style.navWithButton}>
                            {t('login')}
                        </Button>
                    </a>
                </div>
            );
        }
    };

    const renderMembershipAreaButton = () => {
        if (!props.isAlreadyLogin) {
            return null;
        } else {
            return (
                <>
                    <div className="single-menu dropdown">
                        <GlobalNotification />
                    </div>
                    <Dropdown
                        menu={{ items: membershipAreaNavigationItems }}
                        placement="bottomLeft"
                        className="dropdown"
                    >
                        <Button
                            shape="circle"
                            icon={<UserOutlined />}
                            css={style.navProfileButton}
                        />
                    </Dropdown>
                </>
            );
        }
    };

    return (
        <div css={style.base}>
            <div css={style.logoWrapper}>
                <a href={`${OUTSIDE_HOST}`}>
                    <picture>
                        <source srcSet={shareImages['logo.webp']} type="image/webp" />
                        <img src={shareImages['logo.png']} alt="Logo" />
                    </picture>
                </a>
            </div>
            <div css={style.navigationWrapper}>
                <Dropdown
                    menu={{ items: searchNavigationItems }}
                    placement="bottomLeft"
                    className="dropdown"
                >
                    <div>
                        {t('search.trigger')} <CaretDownOutlined />
                    </div>
                </Dropdown>
                <Dropdown
                    menu={{ items: investmentNavigationItems }}
                    placement="bottomLeft"
                    className="dropdown"
                >
                    <div>
                        {t('investment.trigger')} <CaretDownOutlined />
                    </div>
                </Dropdown>
                <div className="single-menu dropdown">
                    <a href="/p/list">
                        {t('professional.trigger')}
                        {/* <CaretDownOutlined /> */}
                    </a>
                </div>
                <Dropdown
                    menu={{ items: otherNavigationItems }}
                    placement="bottomLeft"
                    className="dropdown"
                >
                    <div>
                        {t('other.trigger')} <CaretDownOutlined />
                    </div>
                </Dropdown>
                <Dropdown
                    menu={{ items: comproNavigationItems }}
                    placement="bottomLeft"
                    className="dropdown"
                >
                    <div>
                        {t('compro.trigger')} <CaretDownOutlined />
                    </div>
                </Dropdown>
                <Dropdown
                    menu={{ items: advertisementNavigationItems }}
                    placement="bottomLeft"
                    className="dropdown"
                >
                    <Button css={style.advertiseButton}>
                        {t('solution.trigger')} <CaretDownOutlined />
                    </Button>
                </Dropdown>
                {renderLoginButton()}
                {renderMembershipAreaButton()}
            </div>
        </div>
    );
};

export default NavbarView;
